import fetchWithCsrf from './fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { CurrentUser, UserBase } from 'js/core_post_webpack/models/user';

declare global {
  interface Window {
    // This gets set in _dependencies.html.erb,
    // or simple.html.erb for the LOSWP.
    // It is undefined on logged-out pages.
    $current_user?: string | CurrentUser;
  }
}

// Sometimes all we really care about is that the object we get
// has a property named `id` or `interests`.
// These types let functions that take a user object be less strict.
type UserWithId = Pick<UserBase, 'id'>;
type UserWithInterests = Pick<CurrentUser, 'interests'>;

export const currentUser: CurrentUser | null = (() => {
  const jsonOrUser = window.$current_user;
  if (jsonOrUser == null) return null;
  if (typeof jsonOrUser === 'string') {
    const user: CurrentUser = JSON.parse(jsonOrUser);
    return user;
  } else {
    return jsonOrUser;
  }
})();

export const current = currentUser;

/**
 * Returns `true` if `user` is following `userId`
 */
export const follows = (
  userId: number,
  user: Pick<CurrentUser, 'followee_ids'>,
) => new Set(user.followee_ids).has(userId);

/**
 * ## React Usage
 *
 * @example
 * ```jsx
 *   import { currentUser, getFollowersCount } from 'js/utils/user';
 *   // or
 *   import User from 'js/utils';
 *   // User.currentUser, User.getFollowersCount
 *
 *   const UserFollowers = ({ user }) => {
 *     const [followers, setFollowers] = useState(null);
 *
 *     useEffect(() => {
 *       getFollowersCount(user)
 *         .then(count => setFollowers(count));
 *     }, [user]);
 *
 *     return followers != null
 *       ? <div>{user.display_name} has {followers} followers</div>
 *       : <div>Loading followers count...</div>;
 *   }
 *
 *   <UserFollowers user={currentUser} />;
 * ```
 */
export const getFollowersCount = (user: UserWithId): Promise<number> =>
  fetchWithCsrf(
    routes.api_v0_user_followers_url({ limit: 0, user_id: user.id }),
  )
    .then((res) => res.json())
    .then((data) => data.meta.count);

/**
 * ## React Usage
 *
 * @example
 * ```jsx
 *   import { currentUser, getWorksCount } from 'js/utils/user';
 *   // or
 *   import User from 'js/utils';
 *   // User.currentUser, User.getWorksCount
 *
 *   const UserWorks = ({ user }) => {
 *     const [works, setWorks] = useState(null);
 *
 *     useEffect(() => {
 *       getWorksCount(user)
 *         .then(count => setWorks(count));
 *     }, [user]);
 *
 *     return works != null
 *       ? <div>{user.display_name} has {works} works</div>
 *       : <div>Loading works count...</div>;
 *   }
 *
 *   <UserWorks user={currentUser} />;
 * ```
 */
export const getWorksCount = (user: UserWithId): Promise<number> =>
  fetchWithCsrf(routes.count_api_v0_user_works_url({ user_id: user.id }))
    .then((res) => res.json())
    .then((data) => data.count);

type ViewStats = {
  documents_count: number;
  non_documents_count: number;
};

/**
 * ## React Usage
 *
 * @example
 * ```jsx
 *   import { currentUser, getViewsCount } from 'js/utils/user';
 *   // or
 *   import User from 'js/utils';
 *   // User.currentUser, User.getViewsCount
 *
 *   const UserViews = ({ user }) => {
 *     const [views, setViews] = useState(null);
 *
 *     useEffect(() => {
 *       getViewsCount(user)
 *         .then(count => setViews(count));
 *     }, [user]);
 *
 *     return views != null
 *       ? <div>
 *         {user.display_name} has {views.documents_count} document views
 *         and {views.non_documents_count} other views
 *        </div>
 *       : <div>Loading views count...</div>;
 *   }
 *
 *   <UserViews user={currentUser} />;
 * ```
 */
export const getViewsCount = (user: UserWithId): Promise<ViewStats> =>
  fetchWithCsrf(routes.api_v0_user_stats_url({ user_id: user.id }))
    .then((res) => res.json())
    .then((data) => data.all_time_stats);

/**
 * ## Partial Application
 *
 * @example
 * ```
 * const bigListOfUsers = [...]
 *
 * // 422 is the Computer Science research interest id
 * const interestedInComputerScience = hasInterest.bind(null, 422);
 * const usersInterestedInCS = bigListOfUsers.filter(interestedInComputerScience);
 * ```
 */
export const hasInterest = (interestId: number, user: UserWithInterests) =>
  new Set(user.interests?.map((i) => i.id)).has(interestId);

/**
 * ## Partial Application
 *
 * @example
 * ```jsx
 * const canSeeNewAdminBar = hasRolloutFeature.bind(null, 'can_see_new_admin_bar');
 *
 * <div>
 *   {canSeeNewAdminBar(currentUser) && <AdminBar />}
 * </div>
 * ```
 */
export const hasRolloutFeature = (
  featureName: string,
  user: CurrentUser,
): boolean => new Set(user.active_rollout_features).has(featureName);

export const hasResearchInterests = (user: UserWithInterests) =>
  Number(user.interests?.length) > 0;

export const locale = (user?: CurrentUser | null) =>
  user?.language || window.Aedu.i18next?.languages?.[0];

// Convenience function for many of Wolf Team's 2024-Q1 A/B tests, targeting academic users
const ACADEMIC_ROLES: Array<string | null | undefined> = [
  'faculty',
  'graduate',
];
export const isAcademic = (user: CurrentUser | null): boolean =>
  ACADEMIC_ROLES.includes(user?.current_role);
